import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Grid,
  TextField, Alert, Stepper, Step, StepLabel,
  Select, MenuItem, FormControl, InputLabel, List,
  ListItem, ListItemIcon, ListItemText, Divider,
  CircularProgress, LinearProgress
} from '@mui/material';
import WifiIcon           from '@mui/icons-material/Wifi';
import RouterIcon         from '@mui/icons-material/Router';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import PhoneAndroidIcon   from '@mui/icons-material/PhoneAndroid';
import SettingsIcon       from '@mui/icons-material/Settings';
import FlashOnIcon        from '@mui/icons-material/FlashOn';
import { sitesAPI, sensorsAPI } from '../services/api';
import api                from '../services/api';

const STEPS = [
  'Selectionner le site',
  'Alimenter le capteur',
  'Connexion au capteur',
  'Configurer le capteur',
  'Validation'
];

export default function Provisioning() {
  const [step,       setStep]       = useState(0);
  const [sites,      setSites]      = useState([]);
  const [siteId,     setSiteId]     = useState('');
  const [siteDetail, setSiteDetail] = useState(null);
  const [sensorId,   setSensorId]   = useState('');
  const [role,       setRole]       = useState('ENTRANCE');
  const [checking,   setChecking]   = useState(false);
  const [confirmed,  setConfirmed]  = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  useEffect(() => {
    sitesAPI.list().then(r => setSites(r.data.sites || [])).catch(() => {});
  }, []);

  const loadSite = (id) => {
    setSiteId(id);
    api.get(`/v1/sites/${id}`)
      .then(r => setSiteDetail(r.data))
      .catch(() => {});
  };

  const simulateCheck = () => {
    setChecking(true);
    sensorsAPI.assign(sensorId, { site_id: siteId, role: role })
      .then(() => {
        setChecking(false);
        setConfirmed(true);
        setSuccess('Capteur configure avec succes !');
      })
      .catch(err => {
        setChecking(false);
        setError('Erreur enregistrement : ' + (err.response?.data?.detail || err.message));
      });
  };

  const reset = () => {
    setStep(0); setSiteId(''); setSiteDetail(null);
    setSensorId(''); setRole('ENTRANCE');
    setChecking(false); setConfirmed(false);
    setError(''); setSuccess('');
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="#1F4E79" mb={1}>
        Provisioning capteur WiFi
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Configuration du WiFi sur un nouveau capteur D1 Mini
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 4 }} alternativeLabel>
        {STEPS.map(label => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* STEP 0 */}
      {step === 0 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Pour quel site installez-vous ce capteur ?
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Boutique</InputLabel>
                  <Select value={siteId} label="Boutique"
                    onChange={e => loadSite(e.target.value)}>
                    {sites.map(s => (
                      <MenuItem key={s.site_id} value={s.site_id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Role du capteur</InputLabel>
                  <Select value={role} label="Role du capteur"
                    onChange={e => setRole(e.target.value)}>
                    {['ENTRANCE', 'MIDDLE', 'END', 'CUSTOM'].map(r => (
                      <MenuItem key={r} value={r}>{r}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Sensor ID" required
                  placeholder="Ex: REDIA-ZARA-CASA-A"
                  value={sensorId}
                  onChange={e => setSensorId(e.target.value)}
                  helperText="Format recommande : REDIA-[CLIENT]-[VILLE]-[LETTRE]" />
              </Grid>
            </Grid>
            {siteDetail && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Site : <strong>{siteDetail.site?.name}</strong> —
                {siteDetail.sensors?.length || 0} capteur(s) deja enregistre(s)
              </Alert>
            )}
            <Button variant="contained" size="large"
              sx={{ mt: 3, bgcolor: '#1F4E79', '&:hover': { bgcolor: '#163a5f' } }}
              disabled={!siteId || !sensorId}
              onClick={() => setStep(1)}>
              Etape suivante
            </Button>
          </CardContent>
        </Card>
      )}

      {/* STEP 1 */}
      {step === 1 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <FlashOnIcon sx={{ color: '#FF9800', fontSize: 32 }} />
              <Typography variant="h6" fontWeight={600}>Alimentez le capteur</Typography>
            </Box>
            <List>
              {[
                { icon: <FlashOnIcon sx={{ color: '#FF9800' }} />,
                  text: 'Branchez le capteur D1 Mini sur une prise USB' },
                { icon: <RouterIcon sx={{ color: '#1F4E79' }} />,
                  text: 'Attendez 10 secondes — la LED bleue clignote puis reste allumee' },
                { icon: <WifiIcon sx={{ color: '#4CAF50' }} />,
                  text: 'Le capteur cree un reseau WiFi nomme "REDIA-Setup-XXXX"' },
              ].map((item, i) => (
                <ListItem key={i} sx={{ px: 0 }}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
            </List>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Si la LED ne s'allume pas : verifiez le cable USB.
              Pour reinitialiser un capteur deja configure :
              maintenez le bouton BOOT 5 secondes.
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button variant="outlined" onClick={() => setStep(0)}>Retour</Button>
              <Button variant="contained" size="large"
                sx={{ bgcolor: '#1F4E79', '&:hover': { bgcolor: '#163a5f' } }}
                onClick={() => setStep(2)}>
                LED allumee → Continuer
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <PhoneAndroidIcon sx={{ color: '#2E75B6', fontSize: 32 }} />
              <Typography variant="h6" fontWeight={600}>
                Connectez votre telephone au capteur
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: '#E3F2FD', borderRadius: 2, p: 2 }}>
                  <Typography fontWeight={600} color="#1F4E79" mb={2}>
                    Sur votre smartphone :
                  </Typography>
                  <List dense>
                    {[
                      'Allez dans Parametres > WiFi',
                      'Recherchez le reseau "REDIA-Setup-XXXX"',
                      'Connectez-vous (pas de mot de passe)',
                      'Une page de configuration s\'ouvre automatiquement',
                      'Sinon : ouvrez votre navigateur → 192.168.4.1',
                    ].map((t, i) => (
                      <ListItem key={i} sx={{ px: 0, py: 0.3 }}>
                        <ListItemIcon sx={{ minWidth: 30 }}>
                          <Typography fontWeight={700} color="#1F4E79">{i+1}.</Typography>
                        </ListItemIcon>
                        <ListItemText primary={t}
                          primaryTypographyProps={{ fontSize: '0.88rem' }} />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: '#FFF8E1', borderRadius: 2, p: 2 }}>
                  <Typography fontWeight={600} color="#E65100" mb={1}>
                    Important
                  </Typography>
                  <Typography variant="body2" color="#E65100" mb={1}>
                    Le reseau "REDIA-Setup-XXXX" est temporaire.
                    Il se ferme apres 5 minutes ou apres configuration.
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    IP capteur : <strong>192.168.4.1</strong>
                  </Typography>
                  <Typography variant="body2">
                    Port : <strong>80</strong>
                  </Typography>
                </Card>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button variant="outlined" onClick={() => setStep(1)}>Retour</Button>
              <Button variant="contained" size="large"
                sx={{ bgcolor: '#1F4E79', '&:hover': { bgcolor: '#163a5f' } }}
                onClick={() => setStep(3)}>
                Page de config ouverte → Continuer
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* STEP 3 */}
      {step === 3 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <SettingsIcon sx={{ color: '#1F4E79', fontSize: 32 }} />
              <Typography variant="h6" fontWeight={600}>
                Saisissez ces informations dans la page du capteur
              </Typography>
            </Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Copiez exactement ces valeurs dans la page web ouverte sur votre
              telephone (192.168.4.1)
            </Alert>
            <Grid container spacing={2}>
              {[
                { label: 'Sensor ID',    value: sensorId,    color: '#E3F2FD' },
                { label: 'Site ID',      value: siteId,      color: '#E3F2FD' },
                { label: 'Role',         value: role,        color: '#E3F2FD' },
                { label: 'URL Serveur',  value: 'https://wifi-api.redia-maroc.com', color: '#FFF3E0' },
                { label: 'SSID WiFi',    value: 'WiFi de la boutique', color: '#E8F5E9' },
                { label: 'Mot de passe', value: '(mot de passe boutique)', color: '#E8F5E9' },
              ].map((field, i) => (
                <Grid item xs={12} md={6} key={i}>
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: field.color,
                    border: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {field.label}
                    </Typography>
                    <Typography fontWeight={700} sx={{ wordBreak: 'break-all' }}>
                      {field.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Alert severity="success" sx={{ mt: 3 }}>
              Apres avoir appuye sur "Configurer" dans la page du capteur :
              la LED verte s'allume = capteur connecte ✅
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button variant="outlined" onClick={() => setStep(2)}>Retour</Button>
              <Button variant="contained" size="large"
                sx={{ bgcolor: '#4CAF50', '&:hover': { bgcolor: '#388E3C' } }}
                onClick={() => { setStep(4); simulateCheck(); }}>
                LED verte allumee → Valider
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* STEP 4 */}
      {step === 4 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {checking ? (
              <Box>
                <CircularProgress size={60} sx={{ color: '#1F4E79', mb: 2 }} />
                <Typography variant="h6" fontWeight={600} mb={1}>
                  Verification en cours...
                </Typography>
                <Typography color="text.secondary" mb={2}>
                  On verifie que le capteur communique avec le serveur REDIA
                </Typography>
                <LinearProgress sx={{ borderRadius: 2 }} />
              </Box>
            ) : confirmed ? (
              <Box>
                <CheckCircleIcon sx={{ fontSize: 80, color: '#4CAF50', mb: 2 }} />
                <Typography variant="h5" fontWeight={700} color="#4CAF50" mb={1}>
                  Capteur configure avec succes !
                </Typography>
                <Typography color="text.secondary" mb={3}>
                  Le capteur <strong>{sensorId}</strong> est pret pour le site{' '}
                  <strong>{siteId}</strong>.
                </Typography>
                <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
                  <strong>Prochaines etapes :</strong><br />
                  • Walkthrough — positionner le capteur sur le plan<br />
                  • Calibration — mesures 1m / 3m / 5m / 8m<br />
                  • Supervision — verifier que le capteur est en ligne
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button variant="outlined" onClick={reset}>
                    Configurer un autre capteur
                  </Button>
                  <Button variant="contained"
                    sx={{ bgcolor: '#1F4E79' }}
                    onClick={() => window.location.href = '/supervision'}>
                    Voir la supervision
                  </Button>
                </Box>
              </Box>
            ) : null}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
